import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag, Sparkles, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWishlist, isInWishlist } = useApp();
  const inWishlist = isInWishlist(product.id);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success('Added to cart', {
      description: product.name,
    });
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
    toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist');
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/product/${product.id}`}>
        <Card variant="product" className="group h-full flex flex-col">
          {/* Image Container */}
          <div className="relative aspect-[3/4] overflow-hidden bg-muted">
            <img
              src={product.images?.[0]}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Top Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {discount > 0 && (
                <span className="px-2 py-1 bg-primary-light text-ivory text-xs font-medium rounded-md">
                  -{discount}%
                </span>
              )}
              {product.isCustomizable && (
                <span className="px-2 py-1 bg-silver/90 text-primary-dark text-xs font-medium rounded-md flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Customizable
                </span>
              )}
            </div>

            {/* Wishlist Button */}
            <button
              onClick={handleWishlist}
              className={`absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                inWishlist
                  ? 'bg-primary-light text-ivory'
                  : 'bg-card/80 backdrop-blur-sm text-silver hover:bg-card hover:text-silver-light'
              }`}
              aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart
                className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`}
              />
            </button>

            {/* Quick Actions on Hover */}
            <div className="absolute bottom-3 left-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
              <Button
                variant="ivory"
                size="sm"
                className="flex-1"
                onClick={handleAddToCart}
              >
                <ShoppingBag className="h-4 w-4" />
                Add to Cart
              </Button>
            </div>
          </div>

          {/* Product Info */}
          <div className="p-4 flex flex-col flex-1">
            {/* Rating */}
            {product.rating && (
              <div className="flex items-center gap-1 mb-2">
                <Star className="h-3.5 w-3.5 text-gold fill-gold" />
                <span className="text-xs text-muted-foreground">
                  {product.rating} ({product.reviews})
                </span>
              </div>
            )}

            {/* Name */}
            <h3 className="font-display text-sm text-silver-light line-clamp-2 mb-1 group-hover:text-ivory transition-colors">
              {product.name}
            </h3>
            
            {/* Urdu Name */}
            {product.nameUrdu && (
              <p className="text-xs text-muted-foreground font-body mb-2 text-right" dir="rtl">
                {product.nameUrdu}
              </p>
            )}

            {/* Price - pushed to bottom */}
            <div className="flex items-center gap-2 mt-auto pt-2">
              <span className="font-display text-base text-ivory">
                {formatPrice(product.price)}
              </span>
              {product.originalPrice && (
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.originalPrice)}
                </span>
              )}
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
